import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Box, Dialog, IconButton, Tooltip, Typography } from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

const copyToClipboard = async (text) => {
  // Prefer the modern clipboard API.
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }

  // Minimal fallback for older browsers.
  const ta = document.createElement('textarea')
  ta.value = text
  ta.setAttribute('readonly', '')
  ta.style.position = 'fixed'
  ta.style.left = '-9999px'
  document.body.appendChild(ta)
  ta.select()
  document.execCommand('copy')
  document.body.removeChild(ta)
}

const QrDialog = ({ open, anearEvent, onClose }) => {
  const [copied, setCopied] = useState(false)
  const copiedTimeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current)
    }
  }, [])

  const onCopy = useCallback(async (e) => {
    e?.stopPropagation?.()
    try {
      const url = new URL(anearEvent.short_url, window.location.origin).toString()
      await copyToClipboard(url)
      setCopied(true)
      if (copiedTimeoutRef.current) window.clearTimeout(copiedTimeoutRef.current)
      copiedTimeoutRef.current = window.setTimeout(() => setCopied(false), 1200)
      onClose?.()
    } catch (_e) {
      // best-effort; no-op
    }
  }, [anearEvent, onClose])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      sx={{
        zIndex: 999999, // Higher than child apps container (99999) to ensure QR code appears above child apps
      }}
    >
      {/* Tap anywhere closes (mobile-friendly). */}
      <Box
        onClick={onClose}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: 1.25,
          pt: 3,
          pb: 2.5,
          bgcolor: 'rgba(15,23,42,0.98)',
          color: 'rgba(230,233,239,0.98)',
        }}
      >
        {/* Header: "SCAN TO JOIN" label + Event Title + Copy button */}
        <Box sx={{
          width: '100%',
          maxWidth: '36rem',
          mb: 2,
        }}>
          {/* Subtle label badge - non-competing with title */}
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            mb: 1,
          }}>
            <Typography
              component="span"
              sx={{
                fontSize: '0.75rem',
                fontWeight: 500,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: 'rgba(230,233,239,0.65)',
                px: 1.5,
                py: 0.5,
                bgcolor: 'rgba(255,255,255,0.05)',
                borderRadius: 1,
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              SCAN TO JOIN
            </Typography>
          </Box>

          {/* Event Title - Primary visual hierarchy */}
          <Box sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
          }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 900,
                fontSize: { xs: '1.75rem', sm: '2rem' },
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
                color: 'rgba(230,233,239,0.98)',
              }}
              title={anearEvent?.name}
            >
              {anearEvent?.name}
            </Typography>

            <Tooltip title={copied ? 'Copied' : 'Copy URL'} placement="left" arrow>
              <IconButton
                onClick={onCopy}
                aria-label="Copy event URL"
                sx={{
                  color: 'rgba(230,233,239,0.98)',
                  bgcolor: 'rgba(255,255,255,0.06)',
                  border: '0.12rem solid rgba(255,255,255,0.14)',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box
          sx={{
            width: '100%',
            maxWidth: '36rem',
            aspectRatio: '1 / 1',
            borderRadius: 2,
            overflow: 'hidden',
            border: '0.14rem solid rgba(96,165,250,0.35)',
            boxShadow: '0 18px 46px rgba(0,0,0,0.55)',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}
        >
          <Box
            component="img"
            alt={`QR Code for ${anearEvent?.name}`}
            src={anearEvent?.attributes?.['qr-image-url']}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              // Nicer rendering on scaled QR codes
              imageRendering: 'pixelated',
              bgcolor: 'white',
            }}
          />
        </Box>

        <Typography
          variant="body2"
          sx={{ mt: 2, color: 'rgba(230,233,239,0.78)', textAlign: 'center', maxWidth: '36rem' }}
        >
          Tap anywhere to close
        </Typography>
      </Box>
    </Dialog>
  )
}

QrDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  anearEvent: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default QrDialog
